<template>
  <nav class="navigation bg-white dark:bg-dark-800 border-r border-gray-200 dark:border-dark-700">
    <!-- Header -->
    <div class="nav-header">
      <router-link to="/" class="logo" @click="handleNavClick">
        <img src="@/assets/logo.png" alt="IGW Business Management System" class="logo-img" />
        <span class="logo-text">In God's Will</span>
      </router-link>
    </div>

    <!-- Navigation Links -->
    <div class="nav-links">
      <!-- Main Navigation -->
      <div v-if="hasAnyDashboardPermission" class="nav-section">
        <div class="nav-section-title">DASHBOARD</div>
        <router-link to="/dashboard" class="nav-link" :class="{ 'router-link-active': isCurrentRoute('/dashboard') }"
          @click="handleNavClick">
          <i class="fas fa-chart-line"></i>
          <span>Main Dashboard</span>
        </router-link>
      </div>

      <!-- Personal Section -->
      <div class="nav-section">
        <div class="nav-section-title">PERSONAL</div>
        <router-link to="/profile" class="nav-link" :class="{ 'router-link-active': isCurrentRoute('/profile') }"
          @click="handleNavClick">
          <i class="fas fa-user-circle"></i>
          <span>My Profile</span>
        </router-link>
      </div>

      <!-- Sales & Orders Section -->
      <div v-if="salesItems.length > 0" class="nav-section">
        <div class="nav-section-title">SALES & ORDERS</div>
        <router-link v-for="item in salesItems" :key="item.to" :to="item.to" class="nav-link"
          :class="{ 'router-link-active': isCurrentRoute(item.to) }" @click="handleNavClick">
          <i :class="['fas', item.icon]"></i>
          <span>{{ item.text }}</span>
        </router-link>
      </div>

      <!-- Finance Section -->
      <div v-if="financeItems.length > 0" class="nav-section">
        <div class="nav-section-title">FINANCE</div>
        <router-link v-for="item in financeItems" :key="item.to" :to="item.to" class="nav-link"
          :class="{ 'router-link-active': isCurrentRoute(item.to) }" @click="handleNavClick">
          <i :class="['fas', item.icon]"></i>
          <span>{{ item.text }}</span>
        </router-link>
      </div>

      <!-- Inventory Section -->
      <div v-if="inventoryItems.length > 0" class="nav-section">
        <div class="nav-section-title">INVENTORY</div>
        <router-link v-for="item in inventoryItems" :key="item.to" :to="item.to" class="nav-link"
          :class="{ 'router-link-active': isCurrentRoute(item.to) }" @click="handleNavClick">
          <i :class="['fas', item.icon]"></i>
          <span>{{ item.text }}</span>
        </router-link>
      </div>

      <!-- Reports Section -->
      <div v-if="reportsItems.length > 0" class="nav-section">
        <div class="nav-section-title">REPORTS</div>
        <router-link v-for="item in reportsItems" :key="item.to" :to="item.to" class="nav-link"
          :class="{ 'router-link-active': isCurrentRoute(item.to) }" @click="handleNavClick">
          <i :class="['fas', item.icon]"></i>
          <span>{{ item.text }}</span>
        </router-link>
      </div>

      <!-- Management Section -->
      <div v-if="managementItems.length > 0" class="nav-section">
        <div class="nav-section-title">MANAGEMENT</div>
        <router-link v-for="item in managementItems" :key="item.to" :to="item.to" class="nav-link"
          :class="{ 'router-link-active': isCurrentRoute(item.to) }" @click="handleNavClick">
          <i :class="['fas', item.icon]"></i>
          <span>{{ item.text }}</span>
        </router-link>
      </div>

      <!-- Removed Showcase Section - Now handled by separate igw-showcase-site project -->
    </div>

    <!-- Footer -->
    <div class="nav-footer">
      <div class="user-info" role="button" tabindex="0" @click="openUserProfile" @keyup.enter="openUserProfile">
        <div class="user-avatar">
          <img v-if="user?.profile_picture" :src="user.profile_picture" :alt="user.first_name" class="avatar-image"
            @error="handleAvatarError" />
          <i v-else class="fas fa-user"></i>
        </div>
        <div class="user-details">
          <span class="user-name">
            {{ user?.first_name || user?.firstName }} {{ user?.last_name || user?.lastName }}
          </span>
          <span class="user-role">{{ user?.role || 'User' }}</span>
        </div>
      </div>

      <!-- Action Buttons Row -->
      <div class="flex items-center gap-2 mt-4 pt-3 border-t border-gray-200 dark:border-dark-600">
        <div class="flex-1">
          <ThemeToggle />
        </div>
        <button
          class="logout-btn px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 flex items-center gap-2"
          @click="logout">
          <i class="fas fa-sign-out-alt text-sm"></i>
          <span class="text-sm font-medium">Logout</span>
        </button>
      </div>
    </div>
  </nav>

  <!-- User Profile Modal -->
  <div v-if="showUserProfileModal"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div class="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
      <!-- Modal Header -->
      <div class="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 rounded-t-2xl text-white">
        <div class="flex justify-between items-start">
          <div class="flex items-center gap-4">
            <div
              class="w-16 h-16 bg-white dark:bg-dark-800 bg-opacity-20 rounded-full flex items-center justify-center overflow-hidden">
              <img v-if="user?.profile_picture" :src="user.profile_picture" :alt="user.first_name"
                class="w-full h-full object-cover" @error="handleModalAvatarError" />
              <i v-else class="fas fa-user text-2xl"></i>
            </div>
            <div>
              <h3 class="text-xl font-bold">
                {{ user?.first_name || user?.firstName }} {{ user?.last_name || user?.lastName }}
              </h3>
              <p class="text-indigo-100 text-sm">{{ user?.role || 'User' }}</p>
            </div>
          </div>
          <button @click="closeUserProfile"
            class="w-8 h-8 bg-white dark:bg-dark-800 bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-colors">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Modal Content -->
      <div class="p-6">
        <!-- Loading State -->
        <div v-if="loadingEmployeeData" class="text-center py-8">
          <div
            class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400">
          </div>
          <p class="mt-2 text-gray-600 dark:text-gray-400">Loading employee information...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="employeeDataError" class="text-center py-8">
          <div
            class="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <i class="fas fa-exclamation-triangle text-red-500 dark:text-red-400"></i>
          </div>
          <p class="text-red-600 dark:text-red-400 mb-4">{{ employeeDataError }}</p>
          <button @click="fetchEmployeeData"
            class="bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors">
            Retry
          </button>
        </div>

        <!-- User Information -->
        <div v-else class="space-y-6">
          <!-- Account Information -->
          <div>
            <h4 class="text-lg font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
              <i class="fas fa-id-card text-indigo-600 dark:text-indigo-400"></i>
              Account Information
            </h4>
            <div class="bg-gray-50 dark:bg-dark-700 rounded-lg p-4 space-y-3">
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Email:</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ user?.email || 'Not provided' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Username:</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ user?.username || user?.email ||
                  'Not provided' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Role:</span>
                <span
                  class="px-2 py-1 bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400 rounded-full text-sm font-medium">
                  {{ formatRole(user?.role) }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Status:</span>
                <span class="flex items-center gap-1">
                  <div class="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span class="text-green-600 dark:text-green-400 font-medium">Active</span>
                </span>
              </div>
            </div>
          </div>

          <!-- Employee Information -->
          <div v-if="employeeData">
            <h4 class="text-lg font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
              <i class="fas fa-briefcase text-indigo-600 dark:text-indigo-400"></i>
              Employee Information
            </h4>
            <div class="bg-gray-50 dark:bg-dark-700 rounded-lg p-4 space-y-3">
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Employee ID:</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ employeeData.id || 'Not assigned' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Department:</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ employeeData.department_name ||
                  'Not assigned'
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Position:</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ employeeData.position || 'Not specified'
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Hire Date:</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ formatDate(employeeData.hire_date) }}</span>
              </div>
              <div v-if="employeeData.salary_type" class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Salary Type:</span>
                <span class="font-medium text-gray-900 dark:text-white capitalize">{{
                  employeeData.salary_type.replace('_', ' ') }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Employment Status:</span>
                <span class="px-2 py-1 rounded-full text-sm font-medium" :class="employeeData.employment_status === 'active'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'">
                  {{ employeeData.employment_status || 'Active' }}
                </span>
              </div>
            </div>
          </div>

          <!-- No Employee Record -->
          <div v-else class="text-center py-6">
            <div
              class="w-16 h-16 bg-gray-100 dark:bg-dark-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i class="fas fa-user-tie text-gray-400 dark:text-gray-500 text-2xl"></i>
            </div>
            <h4 class="text-lg font-semibold text-gray-800 dark:text-white mb-2">No Employee Record</h4>
            <p class="text-gray-600 dark:text-gray-400 text-sm">This user account is not linked to an employee profile.
            </p>
          </div>

          <!-- Quick Actions -->
          <div>
            <h4 class="text-lg font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
              <i class="fas fa-bolt text-indigo-600 dark:text-indigo-400"></i>
              Quick Actions
            </h4>
            <div class="grid grid-cols-3 gap-2">
              <button @click="changePassword"
                class="flex flex-col items-center justify-center gap-1.5 p-3 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg transition-colors">
                <i class="fas fa-key text-lg"></i>
                <span class="text-xs font-medium">Password</span>
              </button>
              <button @click="changePIN"
                class="flex flex-col items-center justify-center gap-1.5 p-3 bg-purple-50 hover:bg-purple-100 dark:bg-purple-900/20 dark:hover:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-lg transition-colors">
                <i class="fas fa-lock text-lg"></i>
                <span class="text-xs font-medium">PIN</span>
              </button>
              <button @click="viewFullProfile"
                class="flex flex-col items-center justify-center gap-1.5 p-3 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg transition-colors">
                <i class="fas fa-user-circle text-lg"></i>
                <span class="text-xs font-medium">Profile</span>
              </button>
            </div>
          </div>

          <!-- Last Login Info -->
          <div v-if="user?.last_login"
            class="text-center text-sm text-gray-500 dark:text-gray-400 pt-4 border-t border-gray-200 dark:border-dark-600">
            Last login: {{ formatDate(user.last_login) }}
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="p-6 bg-gray-50 dark:bg-dark-700 rounded-b-2xl">
        <button @click="closeUserProfile"
          class="w-full bg-gray-600 hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors">
          Close
        </button>
      </div>
    </div>
  </div>

  <!-- Password Change Modal -->
  <ChangePasswordModal :is-open="showChangePasswordModal" @close="showChangePasswordModal = false"
    @success="handlePasswordChanged" />

  <!-- PIN Change Modal -->
  <ChangePINModal :is-open="showChangePINModal" @close="showChangePINModal = false" @success="handlePINChanged" />
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useUserEmployeeData } from '@/composables/useMasterData';
import { useToast } from '@/composables/useToast';
import { formatDistanceToNow, parseISO, format } from 'date-fns';
import ThemeToggle from './ThemeToggle.vue';
import ChangePasswordModal from './modals/ChangePasswordModal.vue';
import ChangePINModal from './modals/ChangePINModal.vue';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const toast = useToast();
const { getCurrentUserEmployee, loading } = useUserEmployeeData();

const showUserMenu = ref(false);
const showUserProfileModal = ref(false);
const showChangePasswordModal = ref(false);
const showChangePINModal = ref(false);
const loadingEmployeeData = ref(false);
const employeeDataError = ref(null);
const employeeData = ref(null);

const user = computed(() => authStore.user);

// Check if user has any dashboard permission
const hasAnyDashboardPermission = computed(() => {
  const dashboardPermissions = [
    'dashboard.view_quick_actions',
    'dashboard.view_alerts',
    'dashboard.view_stats'
  ];
  return dashboardPermissions.some(permission => authStore.hasPermission(permission));
});

// Grouped menu items
const salesItems = computed(() => {
  const items = [];

  // Add other sales items based on permissions
  if (authStore.hasPermission('salesdashboard.view')) {
    items.push({
      to: '/sales-dashboard',
      text: 'Sales Dashboard',
      icon: 'fa-chart-line'
    });
  }

  // Only show the Order Board link if the user is not an artist
  if (authStore.hasPermission('orders.view') && authStore.user?.role !== 'artist' && authStore.user?.role !== 'operator') {
    items.push({
      to: '/orders/board',
      text: 'Order Board',
      icon: 'fa-clipboard-list'
    });
  }
  if (authStore.hasPermission('clients.view')) {
    items.push({
      to: '/clients',
      text: 'Clients',
      icon: 'fa-users'
    });
  }



  if (authStore.hasPermission('orders.view') && authStore.user?.role !== 'artist' && authStore.user?.role !== 'operator') {
    items.push({
      to: '/orders',
      text: 'Orders',
      icon: 'fa-shopping-cart'
    });
  }

  if (authStore.hasPermission('quotations.view')) {
    items.push({
      to: '/quotations',
      text: 'Quotations',
      icon: 'fa-file-invoice-dollar'
    });
  }
  // Add printer dashboard for operators, managers, and artists
  if ((authStore.user?.role === 'operator') || authStore.user?.role === 'super_admin' || authStore.user?.role === 'manager' || authStore.user?.role === 'artist') {
    items.push({
      to: '/printer-dashboard',
      text: 'Printer Dashboard',
      icon: 'fa-print'
    });
  }

  // Add layouting dashboard for artists
  if ((authStore.user?.role === 'artist') || authStore.user?.role === 'super_admin' || authStore.user?.role === 'admin' || authStore.user?.role === 'manager') {
    items.push({
      to: '/layouting-dashboard',
      text: 'Artist Dashboard',
      icon: 'fa-paint-brush'
    });
  }

  // Add quality check dashboard for users with quality check permission
  if (authStore.hasPermission('orders.quality_check')) {
    items.push({
      to: '/quality-check-dashboard',
      text: 'Quality Check Dashboard',
      icon: 'fa-check-circle'
    });
  }

  return items;
});

const financeItems = computed(() => [
  {
    to: '/payments',
    icon: 'fa-dollar-sign',
    text: 'Payments',
    permission: 'payments.view'
  },
  {
    to: '/expenses',
    icon: 'fa-credit-card',
    text: 'Expenses',
    permission: 'expenses.view'
  },
  {
    to: '/expenses-dashboard',
    icon: 'fa-chart-bar',
    text: 'Expenses Analytics',
    permission: 'expenses.edit',
  }
].filter(item => !item.permission || (Array.isArray(item.permission) ? item.permission.some(p => authStore.hasPermission(p)) : authStore.hasPermission(item.permission))));

const inventoryItems = computed(() => [
  {
    to: '/products',
    icon: 'fa-box',
    text: 'Products',
    permission: 'products.view'
  },
  {
    to: '/suppliers',
    icon: 'fa-truck',
    text: 'Suppliers',
    permission: 'suppliers.view'
  },
  {
    to: '/requisitions',
    icon: 'fa-file-alt',
    text: 'Requisitions',
    permission: 'requisitions.view'
  }
].filter(item => !item.permission || authStore.hasPermission(item.permission)));

const reportsItems = computed(() => {
  const items = [
    {
      to: '/reports/discrepancies',
      text: 'Discrepancies',
      icon: 'fa-exclamation-triangle',
      permission: 'reports.discrepancies.view'
    },
    {
      to: '/audit-logs',
      icon: 'fa-list-check',
      text: 'Audit Logs',
      permission: 'audit.view'
    }
  ];

  return items.filter(item => !item.permission || authStore.hasPermission(item.permission));
});

const managementItems = computed(() => [
  {
    to: '/employees',
    icon: 'fa-users',
    text: 'Employees',
    permission: ['employees.view', 'employees.manage']
  },
  {
    to: '/payslips',
    icon: 'fa-money-check-alt',
    text: 'Payslips',
    permission: ['PAYSLIPS_VIEW', 'PAYSLIPS_MANAGE']
  },
  {
    to: '/roles',
    icon: 'fa-user-shield',
    text: 'Roles',
    permission: 'roles.view'
  },
  {
    to: '/users',
    icon: 'fa-user-cog',
    text: 'Users',
    permission: 'users.view'
  },
  {
    to: '/management/approvals',
    icon: 'fa-check-circle',
    text: 'Approvals',
    permission: ['payments.approve', 'expenses.approve', 'orders.approve', 'requisitions.approve']
  },
  {
    to: '/storage-gallery',
    icon: 'fa-folder-open',
    text: 'Storage Gallery',
    permission: 'storage.gallery.view'
  },
  // Messaging Center nav item (previously SMS Management)
  {
    to: '/sms-management',
    icon: 'fa-comment-dots',
    text: 'Messaging Center',
    permission: ['sms.view', 'sms.manage']
  }
].filter(item => !item.permission || (Array.isArray(item.permission) ? item.permission.some(p => authStore.hasPermission(p)) : authStore.hasPermission(item.permission))));

// Removed showcaseItems - now handled by separate igw-showcase-site project

const isCurrentRoute = (path) => {
  // For dashboard
  if (path === '/dashboard') {
    return route.path === '/dashboard' || route.path === '/';
  }

  // For exact matches
  if (route.path === path) {
    return true;
  }

  // For nested routes, ensure we don't match parent routes unintentionally
  if (route.path.startsWith(path + '/')) {
    // Don't match parent routes when on a child route
    // e.g. /orders shouldn't match when on /orders/board
    return path.split('/').length === route.path.split('/').length;
  }

  return false;
};

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value;
};

// Handle navigation clicks - close mobile sidebar and prevent duplicate navigations
let isNavigating = false;
const handleNavClick = (event) => {
  // Prevent duplicate navigations
  if (isNavigating) {
    event.preventDefault();
    return;
  }

  // Close mobile sidebar on navigation (emits event to parent)
  if (window.innerWidth < 1024) {
    // Emit custom event to close sidebar in DefaultLayout
    window.dispatchEvent(new CustomEvent('close-sidebar'));
  }

  // Set navigating flag briefly to prevent rapid clicks
  isNavigating = true;
  setTimeout(() => {
    isNavigating = false;
  }, 300);
};

// User Profile Modal Functions
const openUserProfile = () => {
  showUserProfileModal.value = true;
  fetchEmployeeData();
};

const closeUserProfile = () => {
  showUserProfileModal.value = false;
  employeeData.value = null;
  employeeDataError.value = null;
};

const fetchEmployeeData = async () => {
  if (!user.value?.id) return;

  loadingEmployeeData.value = true;
  employeeDataError.value = null;

  try {
    console.log('[Navigation] Fetching employee data from cache for user:', user.value.id);

    // Get employee data using cached lookup by user ID
    const employee = await getCurrentUserEmployee(user.value.id);

    if (employee) {
      employeeData.value = employee;
      console.log('[Navigation] Found employee data in cache:', employee.id);
    } else {
      // No employee record found for this user
      employeeData.value = null;
      console.log('[Navigation] No employee record found for user:', user.value.id);
    }
  } catch (error) {
    console.error('[Navigation] Error fetching employee data:', error);
    employeeDataError.value = 'Failed to load employee information';
  } finally {
    loadingEmployeeData.value = false;
  }
};

// Utility functions
const formatRole = (role) => {
  if (!role) return 'User';

  const roleMap = {
    'super_admin': 'Super Admin',
    'admin': 'Administrator',
    'manager': 'Manager',
    'employee': 'Employee',
    'artist': 'Artist',
    'operator': 'Operator',
    'sales': 'Sales Representative'
  };

  return roleMap[role] || role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const formatDate = (dateString) => {
  if (!dateString) return 'Not available';

  try {
    const date = parseISO(dateString);
    return format(date, 'MMM dd, yyyy');
  } catch (error) {
    return 'Invalid date';
  }
};

// Quick Action Functions
const changePassword = () => {
  closeUserProfile();
  showChangePasswordModal.value = true;
};

const changePIN = () => {
  closeUserProfile();
  showChangePINModal.value = true;
};

const viewFullProfile = () => {
  closeUserProfile();
  router.push('/profile');
};

const handlePasswordChanged = () => {
  toast.success('Password updated successfully!');
};

const handlePINChanged = () => {
  toast.success('PIN updated successfully!');
};

// Handle avatar image errors
const handleAvatarError = (event) => {
  event.target.style.display = 'none';
};

const handleModalAvatarError = (event) => {
  event.target.style.display = 'none';
};

async function logout() {
  await authStore.logout();
  router.push('/auth/login');
}
</script>

<style scoped>
.navigation {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow-x: hidden;
  @apply transition-colors duration-300;
}

.nav-header {
  padding: 1.5rem 1.25rem;
  @apply border-b border-gray-100/50 dark:border-dark-700/50;
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  min-height: 80px;
  @apply transition-colors;
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  @apply text-gray-900 dark:text-dark-100;
  pointer-events: auto;
  user-select: none;
  background: none;
  @apply transition-colors;
}

.logo:hover {
  opacity: 0.8;
}

.logo-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
  background: none;
  flex-shrink: 0;
}

.logo-text {
  font-size: 1.1rem;
  font-weight: 700;
  white-space: nowrap;
  @apply text-gray-900 dark:text-dark-100;
  background: none;
  overflow: hidden;
  text-overflow: ellipsis;
  @apply transition-colors;
}

.nav-links {
  flex: 1;
  padding: 1.5rem 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}

.nav-section {
  margin-bottom: 1.25rem;
}

.nav-section-title {
  padding: 0.75rem 1.5rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  @apply text-gray-500 dark:text-dark-400 transition-colors;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.75rem 1.5rem;
  margin: 0 0.75rem 0.25rem;
  text-decoration: none;
  @apply text-gray-600 dark:text-dark-300;
  transition: all 0.2s ease;
  border-radius: 0.5rem;
  @apply text-gray-600 dark:text-gray-400;
  text-decoration: none;
  font-weight: 500;
  @apply transition-all duration-200;
}

.nav-link:hover {
  @apply bg-surface-100/80 dark:bg-dark-700/50 text-primary-600 dark:text-primary-400;
  transform: translateX(4px);
}

.router-link-active {
  @apply bg-primary-50/80 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-semibold;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.router-link-active i {
  @apply text-primary-600 dark:text-primary-400;
}

.nav-link i {
  width: 20px;
  text-align: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.nav-link span {
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-footer {
  padding: 1.5rem;
  @apply bg-white dark:bg-dark-800 border-t border-gray-100 dark:border-dark-700;
  position: sticky;
  bottom: 0;
  @apply transition-colors;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  margin-bottom: 1rem;
  padding: 0.875rem;
  @apply bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.user-info:hover {
  @apply bg-gray-100 dark:bg-dark-600 border-gray-300 dark:border-dark-500;
}

.user-avatar {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.user-avatar i {
  font-size: 1.25rem;
  color: white;
}

.user-avatar .avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-details {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.user-name {
  font-size: 0.875rem;
  font-weight: 600;
  @apply text-gray-900 dark:text-dark-100;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-role {
  font-size: 0.75rem;
  @apply text-gray-500 dark:text-dark-400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.logout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.875rem;
  @apply bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  font-weight: 500;
}

.logout-btn:hover {
  @apply bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700/50;
}

.logout-btn i {
  font-size: 1.125rem;
}

/* Scrollbar Styling */
.nav-links::-webkit-scrollbar {
  width: 6px;
}

.nav-links::-webkit-scrollbar-track {
  background: transparent;
}

.nav-links::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 3px;
}

.nav-links::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Hide scrollbar for mobile */
@media (max-width: 1024px) {
  .navigation {
    width: 100%;
    box-shadow: none;
    /* Shadow handled by parent aside */
  }

  .nav-header {
    padding-top: 1.5rem;
  }

  .nav-links::-webkit-scrollbar {
    display: none;
  }

  .nav-links {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
}

/* Animations */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.nav-link {
  animation: slideIn 0.3s ease-out;
}
</style>
